import React, { ReactNode, useContext, useLayoutEffect, useRef, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';
import type { DataValue, NodeInstance, NodeInstanceDataPort, NodeInstanceState } from '@glueapp/graph-types';
import { InPortList } from '@/GraphEditor/drawingLayer/nodeComposition/InPortList';
import { OutPortList } from '@/GraphEditor/drawingLayer/nodeComposition/OutPortList';
import { InPortCircle } from './Ports';
import { NodeEventInsulator } from '@/GraphEditor/drawingLayer/graphComponents/NodeInterfaceEventInsulator';
import { usePortPositions } from '@/GraphEditor/drawingLayer/contexts/PortPositionsContext';
import { usePortEvents } from '@/GraphEditor/drawingLayer/contexts/PortEventsContext';
import { usePortOverlayNode } from '@/GraphEditor/drawingLayer/contexts/PortOverlayNodeContext';
import { useTriggerRun } from '@/GraphEditor/drawingLayer/contexts/TriggerRunContext';
import { useNodeSelected } from '@/GraphEditor/drawingLayer/contexts/NodeSelectedContext';
import { getColorFromDataType } from '@/GraphEditor/helpers/getColorFromDataType';
import { RainbowBackgroundAnimation } from '@/base/GlueRainbowBackgroundAnimation';
import { ArrowIcon } from '@/base/ArrowIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, X } from 'lucide-react';
import { ThemeType } from '@/theme/glueTheme';

const RunNodeButton = styled.button`
    position: absolute;
    top: -40px;
    left: 4px;

    &:hover {
        cursor: pointer;
        opacity: 1;
        ${RainbowBackgroundAnimation}
    }

    opacity: 0.5;
    transition: opacity 0.3s ease;

    box-sizing: border-box;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    outline: none;
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${props => props.theme.backgroundColor};
    border: 2px solid ${props => props.theme.separatorColor};
`;

// --- Types ---

type AuthType = 'bearer' | 'basic' | 'apikey' | 'oauth2';

interface NamedEntry {
    id: number;
    name: string;
    // widening the type to allow casting to NodeInstanceState
    [key: string]: DataValue;
}

interface Props {
    name: string;
    nodeInstance: NodeInstance;
    nodeState: Record<string, any>;
    setNodeState: (state: NodeInstanceState) => void;
    renderInherentInPort: () => ReactNode;
    renderInherentOutPort: () => ReactNode;
    children?: ReactNode;
}

// --- Helpers ---

function deriveNextId(headers: NamedEntry[], queryParams: NamedEntry[]): number {
    let max = 0;
    for (const header of headers) if (header.id > max) max = header.id;
    for (const param of queryParams) if (param.id > max) max = param.id;
    return max + 1;
}

// --- Inline Port Row (display / edit modes) ---

interface InlinePortRowProps {
    port: NodeInstanceDataPort | undefined;
    entryName: string;
    isEditing: boolean;
    editingValue: string;
    onStartEditing: () => void;
    onNameChange: (value: string) => void;
    onCommit: () => void;
    onRemove: () => void;
    placeholder: string;
}

const InlinePortRow: React.FC<InlinePortRowProps> = ({
    port,
    entryName,
    isEditing,
    editingValue,
    onStartEditing,
    onNameChange,
    onCommit,
    onRemove,
    placeholder,
}) => {
    const iconRef = useRef<HTMLDivElement | null>(null);
    const { setInPortPosition } = usePortPositions();
    const { onInPortMouseUp, onInPortMouseDown } = usePortEvents();
    const { openOverlay } = usePortOverlayNode();
    const theme = useContext<ThemeType>(ThemeContext);

    useLayoutEffect(() => {
        if (!port) return;
        const el = iconRef.current;
        if (el) {
            const rect = el.getBoundingClientRect();
            setInPortPosition(port.name, {
                x: rect.x + rect.width / 2,
                y: rect.y + rect.height / 2,
            });
        }
    });

    const portCircle = port ? (
        <InPortCircle
            ref={iconRef}
            color={getColorFromDataType(port.type, theme)}
            onPointerDown={(e: React.MouseEvent) => {
                e.stopPropagation();
                onInPortMouseDown(e, port.name);
            }}
            onPointerUp={(e: React.MouseEvent) => {
                e.stopPropagation();
                onInPortMouseUp(e, port.name);
            }}
        />
    ) : null;

    return (
        <div className="group/row relative flex items-center gap-2 py-1.5 pl-2.5 pr-2.5">
            {portCircle}
            <div className="h-6 leading-6">
                <div className="w-[144px]">
                    {isEditing ? (
                        <NodeEventInsulator>
                            <input
                                size={1}
                                className="h-6 w-full rounded border border-input bg-transparent px-1.5 text-[inherit] placeholder:text-current placeholder:opacity-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                placeholder={placeholder}
                                value={editingValue}
                                onChange={e => onNameChange(e.target.value)}
                                onBlur={onCommit}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') onCommit();
                                }}
                                autoFocus
                            />
                        </NodeEventInsulator>
                    ) : (
                        <span
                            className="block truncate cursor-pointer hover:underline"
                            onPointerDown={e => {
                                if (!port) return;
                                e.stopPropagation();
                                e.preventDefault();
                                openOverlay(port.name, 'in');
                            }}
                        >
                            {entryName}
                        </span>
                    )}
                </div>
            </div>
            <NodeEventInsulator>
                <div className="flex items-center gap-0.5">
                    {isEditing ? (
                        <div className="w-6 shrink-0" />
                    ) : (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 shrink-0 opacity-0 group-hover/row:opacity-60 hover:!opacity-100 transition-opacity"
                            onClick={onStartEditing}
                        >
                            <Pencil className="h-3 w-3" />
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 shrink-0 opacity-0 group-hover/row:opacity-60 hover:!opacity-100 transition-opacity"
                        onClick={onRemove}
                    >
                        <X className="h-3 w-3" />
                    </Button>
                </div>
            </NodeEventInsulator>
        </div>
    );
};

// --- Section Label ---

const SectionLabel: React.FC<{ children: ReactNode; rightSlot?: ReactNode; labelColor: string }> = ({
    children,
    rightSlot,
    labelColor,
}) => (
    <div className="flex items-center justify-between px-3 py-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: labelColor }}>
            {children}
        </span>
        {rightSlot && <NodeEventInsulator>{rightSlot}</NodeEventInsulator>}
    </div>
);

// --- Main Content Layout ---

export const HttpRequestContentLayout: React.FC<Props> = ({
    name,
    nodeInstance,
    nodeState,
    setNodeState,
    renderInherentInPort,
    renderInherentOutPort,
    children,
}) => {
    const triggerRun = useTriggerRun();
    const selected = useNodeSelected();
    const theme = useContext<ThemeType>(ThemeContext);

    const separatorStyle = { borderColor: theme.separatorColor };
    const labelColor = theme.subtleContrastColor;

    // --- State from nodeInstance.state ---
    const headers: NamedEntry[] = nodeState.headers || [];
    const queryParams: NamedEntry[] = nodeState.queryParams || [];
    const authEnabled: boolean = nodeState.authEnabled || false;
    const authType: AuthType = nodeState.authType || 'bearer';
    const apiKeyHeaderName: string = nodeState.apiKeyHeaderName || 'X-API-Key';

    // Local editing state
    const [editingHeaderNames, setEditingHeaderNames] = useState<Record<number, string>>({});
    const [editingQueryNames, setEditingQueryNames] = useState<Record<number, string>>({});
    const [editingApiKeyHeader, setEditingApiKeyHeader] = useState<string | null>(null);

    // --- Port Classification ---
    const inPorts = nodeInstance.inPorts;
    const outPorts = nodeInstance.outPorts;

    const urlPort = inPorts.find(p => p.name === 'URL');
    const methodPort = inPorts.find(p => p.name === 'Method');
    const pathPorts = inPorts.filter(p => p.name.startsWith('Path: '));
    const headerPorts = inPorts.filter(p => p.name.startsWith('Header: '));
    const queryPorts = inPorts.filter(p => p.name.startsWith('Query: '));
    const bodyShapePort = inPorts.find(p => p.name === 'Body Shape');
    const bodyPort = inPorts.find(p => p.name === 'Body');
    const authPorts = inPorts.filter(p => p.name.startsWith('Auth '));
    const responseShapePort = inPorts.find(p => p.name === 'Response Shape');

    const responsePort = outPorts.find(p => p.name === 'Response');
    const statusCodePort = outPorts.find(p => p.name === 'Status Code');

    // Group ports for InPortList / OutPortList rendering
    const requestInPorts = [urlPort, ...pathPorts, methodPort].filter(Boolean) as NodeInstanceDataPort[];
    const bodyPorts = [bodyShapePort, bodyPort].filter(Boolean) as NodeInstanceDataPort[];
    const responseInPorts = responseShapePort ? [responseShapePort] : [];
    const responseOutPorts = [responsePort, statusCodePort].filter(Boolean) as NodeInstanceDataPort[];

    // --- Header Handlers ---
    const addHeader = () => {
        const id = deriveNextId(headers, queryParams);
        const newHeaders = [...headers, { id, name: '' }];
        setEditingHeaderNames(prev => ({ ...prev, [id]: '' }));
        setNodeState({ ...nodeState, headers: newHeaders });
    };

    const removeHeader = (id: number) => {
        const newHeaders = headers.filter(header => header.id !== id);
        setEditingHeaderNames(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        setNodeState({ ...nodeState, headers: newHeaders });
    };

    const commitHeaderName = (id: number) => {
        const editedName = editingHeaderNames[id];
        if (editedName === undefined) return;
        setEditingHeaderNames(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        if (!editedName.trim()) {
            // Empty name — remove the entry
            const newHeaders = headers.filter(header => header.id !== id);
            setNodeState({ ...nodeState, headers: newHeaders });
        } else {
            const newHeaders = headers.map(header => (header.id === id ? { ...header, name: editedName } : header));
            setNodeState({ ...nodeState, headers: newHeaders });
        }
    };

    // --- Query Param Handlers ---
    const addQueryParam = () => {
        const id = deriveNextId(headers, queryParams);
        const newParams = [...queryParams, { id, name: '' }];
        setEditingQueryNames(prev => ({ ...prev, [id]: '' }));
        setNodeState({ ...nodeState, queryParams: newParams });
    };

    const removeQueryParam = (id: number) => {
        const newParams = queryParams.filter(param => param.id !== id);
        setEditingQueryNames(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        setNodeState({ ...nodeState, queryParams: newParams });
    };

    const commitQueryName = (id: number) => {
        const editedName = editingQueryNames[id];
        if (editedName === undefined) return;
        setEditingQueryNames(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        if (!editedName.trim()) {
            // Empty name — remove the entry
            const newParams = queryParams.filter(param => param.id !== id);
            setNodeState({ ...nodeState, queryParams: newParams });
        } else {
            const newParams = queryParams.map(param => (param.id === id ? { ...param, name: editedName } : param));
            setNodeState({ ...nodeState, queryParams: newParams });
        }
    };

    // --- Auth Handlers ---
    const toggleAuth = (checked: boolean) => {
        setNodeState({ ...nodeState, authEnabled: checked });
    };

    const changeAuthType = (newType: AuthType) => {
        setNodeState({ ...nodeState, authType: newType });
    };

    const commitApiKeyHeaderName = () => {
        if (editingApiKeyHeader === null) return;
        setNodeState({ ...nodeState, apiKeyHeaderName: editingApiKeyHeader });
        setEditingApiKeyHeader(null);
    };

    // --- Render ---

    const renderManualTrigger = () => {
        if (selected) {
            return (
                <RunNodeButton aria-label={`Run block ${name}`} onClick={triggerRun} onPointerDown={event => event.stopPropagation()}>
                    <ArrowIcon />
                </RunNodeButton>
            );
        }
        return null;
    };

    const addButton = (onClick: () => void) => (
        <Button variant="outline" size="sm" className="h-6 w-6 p-0 text-xs" onClick={onClick}>
            +
        </Button>
    );

    return (
        <>
            {renderManualTrigger()}
            {/* Header */}
            <div className="flex justify-between pt-2.5 pb-2">
                {renderInherentInPort()}
                <h2 className="m-0 font-medium text-[1.2em] grow text-left px-2">{name}</h2>
                {renderInherentOutPort()}
            </div>

            {/* Two-column body */}
            <div className="flex border-t" style={separatorStyle}>
                {/* Left column — request config */}
                <div className="flex-1 min-w-0">
                    {/* REQUEST Section */}
                    <div className="pb-1">
                        <SectionLabel labelColor={labelColor}>Request</SectionLabel>
                        <InPortList ports={requestInPorts} />
                    </div>

                    {/* HEADERS Section */}
                    <div className="border-t pb-2" style={separatorStyle}>
                        <SectionLabel labelColor={labelColor} rightSlot={addButton(addHeader)}>
                            Headers
                        </SectionLabel>
                        {headers.map(header => {
                            const port = headerPorts.find(port => port.name === `Header: ${header.name}`);
                            const isEditing = editingHeaderNames[header.id] !== undefined;
                            return (
                                <InlinePortRow
                                    key={header.id}
                                    port={port}
                                    entryName={header.name}
                                    isEditing={isEditing}
                                    editingValue={editingHeaderNames[header.id] ?? header.name}
                                    onStartEditing={() => setEditingHeaderNames(prev => ({ ...prev, [header.id]: header.name }))}
                                    onNameChange={value => setEditingHeaderNames(prev => ({ ...prev, [header.id]: value }))}
                                    onCommit={() => commitHeaderName(header.id)}
                                    onRemove={() => removeHeader(header.id)}
                                    placeholder="Header name"
                                />
                            );
                        })}
                    </div>

                    {/* QUERY PARAMS Section */}
                    <div className="border-t pb-2" style={separatorStyle}>
                        <SectionLabel labelColor={labelColor} rightSlot={addButton(addQueryParam)}>
                            Query Params
                        </SectionLabel>
                        {queryParams.map(param => {
                            const port = queryPorts.find(port => port.name === `Query: ${param.name}`);
                            const isEditing = editingQueryNames[param.id] !== undefined;
                            return (
                                <InlinePortRow
                                    key={param.id}
                                    port={port}
                                    entryName={param.name}
                                    isEditing={isEditing}
                                    editingValue={editingQueryNames[param.id] ?? param.name}
                                    onStartEditing={() => setEditingQueryNames(prev => ({ ...prev, [param.id]: param.name }))}
                                    onNameChange={value => setEditingQueryNames(prev => ({ ...prev, [param.id]: value }))}
                                    onCommit={() => commitQueryName(param.id)}
                                    onRemove={() => removeQueryParam(param.id)}
                                    placeholder="Param name"
                                />
                            );
                        })}
                    </div>

                    {/* BODY Section (only if body ports exist) */}
                    {bodyPorts.length > 0 && (
                        <div className="border-t pb-1" style={separatorStyle}>
                            <SectionLabel labelColor={labelColor}>Body</SectionLabel>
                            <InPortList ports={bodyPorts} />
                        </div>
                    )}

                    {/* AUTH Section */}
                    <div className="border-t pb-1" style={separatorStyle}>
                        <div className="flex items-center justify-between px-3 py-2">
                            <NodeEventInsulator>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <Checkbox checked={authEnabled} onCheckedChange={checked => toggleAuth(!!checked)} />
                                    <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: labelColor }}>
                                        Auth
                                    </span>
                                </label>
                            </NodeEventInsulator>
                            {authEnabled && (
                                <NodeEventInsulator>
                                    <Select value={authType} onValueChange={value => changeAuthType(value as AuthType)}>
                                        <SelectTrigger className="h-6 text-[11px] w-auto min-w-[110px]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="bearer">Bearer Token</SelectItem>
                                            <SelectItem value="basic">Basic Auth</SelectItem>
                                            <SelectItem value="apikey">API Key</SelectItem>
                                            <SelectItem value="oauth2">OAuth2 Client</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </NodeEventInsulator>
                            )}
                        </div>
                        {authEnabled && (
                            <>
                                {authType === 'apikey' && (
                                    <div className="px-3 pb-1.5">
                                        <NodeEventInsulator>
                                            <Input
                                                className="h-6 text-xs"
                                                placeholder="Header name (e.g. X-API-Key)"
                                                value={editingApiKeyHeader !== null ? editingApiKeyHeader : apiKeyHeaderName}
                                                onChange={e => setEditingApiKeyHeader(e.target.value)}
                                                onBlur={commitApiKeyHeaderName}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') commitApiKeyHeaderName();
                                                }}
                                            />
                                        </NodeEventInsulator>
                                    </div>
                                )}
                                <InPortList ports={authPorts} />
                            </>
                        )}
                    </div>
                </div>

                {/* Right column — response */}
                <div className="min-w-[140px] border-l pb-1" style={separatorStyle}>
                    <SectionLabel labelColor={labelColor}>Response</SectionLabel>
                    <InPortList ports={responseInPorts} />
                    <OutPortList ports={responseOutPorts} />
                </div>
            </div>

            {children}
        </>
    );
};
